import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Shield, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RoleManagement({ user }) {
  const [roles, setRoles] = useState({ system_roles: [], custom_roles: [] });
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_super_admin_module: false,
    permissions: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions')
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (resource, action) => {
    setFormData(prev => {
      const newPerms = { ...prev.permissions };
      if (!newPerms[resource]) {
        newPerms[resource] = [];
      }
      if (newPerms[resource].includes(action)) {
        newPerms[resource] = newPerms[resource].filter(a => a !== action);
        if (newPerms[resource].length === 0) {
          delete newPerms[resource];
        }
      } else {
        newPerms[resource] = [...newPerms[resource], action];
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.patch(`/roles/${editingRole.id}`, formData);
        toast.success('Role updated successfully');
      } else {
        await api.post('/roles', formData);
        toast.success('Role created successfully');
      }
      setDialogOpen(false);
      setEditingRole(null);
      setFormData({ name: '', description: '', is_super_admin_module: false, permissions: {} });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save role'));
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      is_super_admin_module: role.is_super_admin_module || false,
      permissions: role.permissions
    });
    setDialogOpen(true);
  };

  const handleDelete = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success('Role deleted successfully');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete role'));
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading roles...</div>;
  }

  return (
    <div className="space-y-6" data-testid="role-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Role Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage system and custom roles with permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingRole(null);
              setFormData({ name: '', description: '', is_super_admin_module: false, permissions: {} });
            }} data-testid="create-role-button">
              <Plus className="h-4 w-4 mr-2" />
              Create Custom Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Role Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    disabled={editingRole?.is_system}
                    data-testid="role-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    data-testid="role-description-input"
                  />
                </div>
                
                {/* Super Admin Module Role Checkbox */}
                {user?.role === 'super_admin' && !editingRole?.is_system && (
                  <div className="flex items-center space-x-3 p-4 rounded-lg border bg-purple-50 border-purple-200">
                    <Checkbox
                      id="is_super_admin_module"
                      checked={formData.is_super_admin_module}
                      onCheckedChange={(checked) => setFormData({...formData, is_super_admin_module: checked})}
                      data-testid="super-admin-module-checkbox"
                    />
                    <div>
                      <label htmlFor="is_super_admin_module" className="font-medium cursor-pointer">
                        Super Admin Module Role
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Enable this for roles that are used at Super Admin/Platform level (e.g., company management, platform settings)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(permissions).map(([resource, actions]) => (
                    <Card key={resource} className="p-4">
                      <h4 className="font-semibold capitalize mb-3">{resource.replace('_', ' ')}</h4>
                      <div className="space-y-2">
                        {actions.map(action => (
                          <div key={action} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${resource}-${action}`}
                              checked={formData.permissions[resource]?.includes(action) || false}
                              onCheckedChange={() => handlePermissionToggle(resource, action)}
                              data-testid={`perm-${resource}-${action}`}
                            />
                            <label
                              htmlFor={`${resource}-${action}`}
                              className="text-sm cursor-pointer"
                            >
                              {action.replace('_', ' ')}
                            </label>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-role-button">
                  {editingRole ? 'Update Role' : 'Create Role'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* System Roles */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>System Roles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.system_roles.map(role => (
              <Card key={role.id} className="p-4" data-testid={`system-role-${role.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="h-5 w-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">{role.name}</h3>
                    <Badge variant="secondary" className="mt-1">System Role</Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {Object.keys(role.permissions === 'all' ? permissions : role.permissions).length} resources
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(role)}
                  data-testid={`edit-system-role-${role.id}`}
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit Permissions
                </Button>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Roles */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Custom Roles</CardTitle>
        </CardHeader>
        <CardContent>
          {roles.custom_roles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No custom roles created yet
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.custom_roles.map(role => (
                <Card key={role.id} className="p-4 hover-lift" data-testid={`custom-role-${role.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-purple-600" />
                      <div>
                        <h3 className="font-semibold">{role.name}</h3>
                        <p className="text-xs text-muted-foreground">{role.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">
                    {Object.keys(role.permissions).length} resources
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(role)}
                      data-testid={`edit-role-${role.id}`}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(role.id)}
                      data-testid={`delete-role-${role.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
